import { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'

import { isEmpty, debounce } from 'lodash'

import { getRooftopId, getCustomer } from 'selectors/appStatusSelectors'
import { isCustomerKnown } from 'utils'

import { hydrateCustomer } from 'actions/appStatusActions'
import { deleteMagicCookie } from 'actions/naughtyActions'

import { getMagicCustomer } from 'utils/MagicCookie'
import { NaughtyDB } from 'utils/NaughtyDB'

import { tagDiff, groupCollapsed, info, groupEnd } from 'utils'

import { gtmPushCustomerKnown } from 'actions/naughtyActions'

import GTMPush from './GTMPush'

/* getDurableCustomer
 *
 * Gets the Customer from the leavings of other systems to bring the appstate up
 * to speed. Combines data from the magicCustomer and the immortaldb. Returns
 * null if if the result is labelled as a dealer/ghosted user
 */
const getDurableCustomer = async rooftopId => {
  let magicCustomer = getMagicCustomer(),
    immortalCustomer = await NaughtyDB.getCustomer(rooftopId),
    customer = {
      ...magicCustomer,
      ...immortalCustomer,
    }

  return customer._entityType === 'dealer' ? null : customer
}

const startRead = groupCollapsed(`[ECHELON/READ]`),
  endRead = groupEnd(`[ECHELON/READ]`)

const startWrite = groupCollapsed(`[ECHELON/WRITE]`),
  endWrite = groupEnd(`[ECHELON/WRITE]`)

export const useDebounce = (obj, wait = 1000) => {
  const [state, setState] = useState(obj)

  const setDebouncedState = _val => {
    debounced(_val)
  }

  const debounced = useCallback(
    debounce(_prop => {
      setState(_prop)
    }, wait),
    [],
  )

  return [state, setDebouncedState]
}

/*
 *
 * Echelon
 *
 * The purpose of this component is to:
 *
 *  #1: On inital load, read out the Immortal/Magic cookie and hydrate the app
 *      state with customer data
 *  #2: Write to the immortal cookie after the app state customer changes
 *
 *  The cookie should be written last since it only exists for durability.
 *  Previous versions of the customer tracking have treated the cookie writing as
 *  the first thing to do, which causes hangups and timing issues and race
 *  conditions since it has to happen ansyncronously through ImmortalDB
 *
 *  Could be extended later for other data keys if needed
 */
export function Echelon(props) {
  let {
      rooftopId,
      writeCustomerToAppState,
      onChange = () => {},
      deleteMagicCookie,
      gtmPushCustomerKnown,
    } = props,
    [customer, setCustomer] = useDebounce(props.customer),
    [previousCustomer, setPreviousCustomer] = useState({}),
    [wet, setWet] = useState(), // esures that the loading is complete before any writing to the durable data
    readCustomer = useCallback(async () => {
      let customerData = await getDurableCustomer(rooftopId)

      startRead()
      info(customerData)
      endRead()

      writeCustomerToAppState(customerData)
      deleteMagicCookie()

      setWet(true)
    }, [deleteMagicCookie, rooftopId, writeCustomerToAppState]),
    writeCustomer = useCallback(() => {
      let diff = tagDiff()(customer, previousCustomer)

      if (isEmpty(diff) || isEmpty(customer)) return

      startWrite()
      info('Changes -> ', diff)
      info('New tag -> ', customer)
      endWrite()

      NaughtyDB.tagCustomer(rooftopId, customer)
      setPreviousCustomer({ ...customer })

      onChange({ customer, previousCustomer })
    }, [customer, previousCustomer, rooftopId, onChange])

  useEffect(() => {
    if (
      !isEmpty(previousCustomer) &&
      !isEmpty(customer) &&
      !isCustomerKnown(previousCustomer) &&
      isCustomerKnown(customer)
    ) {
      info('!!! CUSTOMER HAS NOW BECOME KNOWN')

      gtmPushCustomerKnown()
    }
  }, [customer, previousCustomer, wet, gtmPushCustomerKnown])

  // On inital load, pull together the durable customer info and hydrate the app state
  useEffect(() => {
    readCustomer()
  }, [readCustomer])

  // update the durable customer on changes to the app state copy
  useEffect(() => {
    wet && writeCustomer()
  }, [wet, writeCustomer])

  useEffect(() => {
    setCustomer(props.customer)
  }, [props.customer, setCustomer])

  return null
}

function mapStateToProps(state) {
  let ret = {
    customer: getCustomer(state),
    rooftopId: getRooftopId(state),
  }

  return ret
}

export default connect(
  mapStateToProps,
  {
    writeCustomerToAppState: hydrateCustomer,
    deleteMagicCookie,
    gtmPushCustomerKnown,
  },
)(Echelon)

export { GTMPush }
